// AuthorPage — the public /author/{slug} profile: avatar, name, bio and
// the author's published articles. Works for the "ProProp Data Lab"
// house identity and for any agent that has a slug (epic PROP-41 / S5).

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getPublicAuthor, type ArticleWire, type PublicAuthor } from "@/lib/articlesApi";
import { ApiError } from "@/lib/api";

export function AuthorPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [author, setAuthor] = useState<PublicAuthor | null>(null);
  const [articles, setArticles] = useState<ArticleWire[]>([]);
  const [loading, setLoading] = useState(true);
  // PROP-204: distinguish a real 404 from a service failure so an outage
  // isn't masked as "author not found".
  const [errKind, setErrKind] = useState<"notfound" | "error" | null>(null);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setLoading(true);
    setErrKind(null);
    getPublicAuthor(slug)
      .then((resp) => {
        if (cancelled) return;
        setAuthor(resp.author);
        setArticles(resp.articles ?? []);
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        // Only a real 404 means "no such author"; anything else (5xx /
        // network) is a service failure and must not masquerade as 404.
        const status = e instanceof ApiError ? e.status : undefined;
        setErrKind(status === 404 ? "notfound" : "error");
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const initials = useMemo(() => {
    const parts = (author?.name ?? "").trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "?";
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }, [author?.name]);

  return (
    <div style={page}>
      <div style={{ maxWidth: 760, margin: "0 auto", padding: "28px 24px 80px" }}>
        <button type="button" onClick={() => navigate("/explore")} style={backBtn}>
          ← Newsroom
        </button>

        {loading && <div style={muted}>Loading…</div>}

        {!loading && (errKind || !author) && (
          <div style={{ marginTop: 28 }}>
            {errKind === "error" ? (
              <>
                <h1 style={h1}>Couldn't load this author</h1>
                <p style={muted}>
                  Something went wrong on our end. Please try again in a moment.
                </p>
              </>
            ) : (
              <>
                <h1 style={h1}>Author not found</h1>
                <p style={muted}>
                  We couldn't find an author at <code>/author/{slug}</code>.
                </p>
              </>
            )}
          </div>
        )}

        {!loading && author && (
          <>
            {/* identity header */}
            <header
              style={{
                display: "flex",
                alignItems: "center",
                gap: 16,
                marginTop: 22,
                paddingBottom: 22,
                borderBottom: "1px solid var(--rule)",
              }}
            >
              {author.photo_url ? (
                <img src={author.photo_url} alt="" style={avatarImg} />
              ) : (
                <span style={avatarFallback}>{initials}</span>
              )}
              <div style={{ minWidth: 0 }}>
                {author.kind === "house" && <div style={kicker}>ProProp · House desk</div>}
                <h1 style={h1}>{author.name}</h1>
                {author.bio && <p style={bio}>{author.bio}</p>}
              </div>
            </header>

            {/* article list */}
            <h2 style={h2}>
              Published articles
              <span style={{ color: "var(--ink-3)", fontWeight: 400 }}> · {articles.length}</span>
            </h2>
            {articles.length === 0 ? (
              <div style={muted}>No published articles yet.</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {articles.map((a) => (
                  <article key={a.id} style={row}>
                    <span style={kindChip}>{a.kind}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={rowTitle}>{a.title}</div>
                      {a.dek && <div style={rowDek}>{a.dek}</div>}
                    </div>
                    <span style={rowMeta}>{formatDate(a.published_at ?? a.updated_at)}</span>
                  </article>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-SG", { year: "numeric", month: "short", day: "numeric" });
}

const page: React.CSSProperties = {
  minHeight: "100dvh",
  background: "var(--bg)",
  color: "var(--ink)",
  fontFamily: "var(--font-ui)",
};
const backBtn: React.CSSProperties = {
  background: "transparent",
  border: "1px solid var(--rule)",
  borderRadius: 7,
  padding: "6px 12px",
  fontFamily: "var(--font-ui)",
  fontSize: 12,
  color: "var(--ink-2)",
  cursor: "pointer",
};
const avatarImg: React.CSSProperties = {
  width: 72,
  height: 72,
  borderRadius: 999,
  objectFit: "cover",
  flexShrink: 0,
  border: "1px solid var(--rule)",
};
const avatarFallback: React.CSSProperties = {
  width: 72,
  height: 72,
  borderRadius: 999,
  flexShrink: 0,
  background: "linear-gradient(140deg, var(--accent), var(--accent-2, var(--accent)))",
  color: "#f4f1ea",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: "var(--font-serif)",
  fontSize: 26,
  fontWeight: 600,
};
const kicker: React.CSSProperties = {
  fontFamily: "var(--font-ui)",
  fontSize: 10.5,
  fontWeight: 600,
  letterSpacing: "0.13em",
  textTransform: "uppercase",
  color: "var(--accent)",
  marginBottom: 4,
};
const h1: React.CSSProperties = {
  fontFamily: "var(--font-serif)",
  fontSize: 30,
  fontWeight: 500,
  letterSpacing: "-0.025em",
  margin: 0,
  color: "var(--ink)",
  lineHeight: 1.12,
};
const bio: React.CSSProperties = {
  fontFamily: "var(--font-serif)",
  fontSize: 14.5,
  fontStyle: "italic",
  color: "var(--ink-2)",
  margin: "8px 0 0",
  lineHeight: 1.5,
};
const h2: React.CSSProperties = {
  fontFamily: "var(--font-ui)",
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: "var(--ink-3)",
  margin: "26px 0 10px",
};
const row: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "14px 4px",
  borderBottom: "1px solid var(--rule)",
};
const kindChip: React.CSSProperties = {
  flexShrink: 0,
  padding: "2px 8px",
  borderRadius: 4,
  background: "var(--surface-2, var(--surface))",
  border: "1px solid var(--rule)",
  fontFamily: "var(--font-ui)",
  fontSize: 9.5,
  fontWeight: 700,
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  color: "var(--ink-3)",
};
const rowTitle: React.CSSProperties = {
  fontFamily: "var(--font-serif)",
  fontSize: 16,
  fontWeight: 500,
  color: "var(--ink)",
  letterSpacing: "-0.015em",
  lineHeight: 1.3,
};
const rowDek: React.CSSProperties = {
  fontFamily: "var(--font-serif)",
  fontSize: 13,
  fontStyle: "italic",
  color: "var(--ink-3)",
  marginTop: 3,
  lineHeight: 1.45,
};
const rowMeta: React.CSSProperties = {
  flexShrink: 0,
  fontFamily: "var(--font-ui)",
  fontSize: 11,
  color: "var(--ink-4)",
};
const muted: React.CSSProperties = {
  fontFamily: "var(--font-serif)",
  fontStyle: "italic",
  fontSize: 14,
  color: "var(--ink-3)",
  marginTop: 20,
};
